import { useState, useEffect, useReducer, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import axios from 'axios';
import MessageBox from '../components/MessageBox';
import Filter from '../components/Filter';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import SeoUpdater from 'components/SeoUpdater';
import { Store } from 'Store';
import { shopType as shopTypeEnum } from 'utils/types.js';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        products: action.payload,
        loading: false,
      };
    case 'FETCH_SUCCESS_SETTINGS':
      return {
        ...state,
        settings: action.payload.settings,
        productsCount: action.payload.productsCount,
        loadingSettings: false,
      };
    case 'RELOAD_PRODUCTS':
      return { ...state, products: action.payload };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'FETCH_MORE_REQUEST':
      return { ...state, loadMore: true };
    case 'FETCH_MORE_SUCCESS':
      return {
        ...state,
        loadMore: false,
        products: action.payload,
      };
    case 'RESET':
      return {
        ...state,
        products: [],
        productsCount: 0,
      };
    case 'STOP_LOADING':
      return {
        ...state,
        loading: false,
      };
    default:
      return state;
  }
};

function ShopScreen(props) {
  const { shopType } = props;

  const { pathname } = useLocation();

  const [
    { loading, loadingSettings, error, products, settings, productsCount },
    dispatch,
  ] = useReducer(reducer, {
    products: [],
    loading: true,
    loadingSettings: true,
    error: '',
    settings: undefined,
    productsCount: 0,
  });

  const { state } = useContext(Store);
  const { id, cart } = state;

  useEffect(() => {
    const userAction = async () => {
      await axios.get('/api/users/opensite', {
        headers: {
          userid: id,
          cart: cart.cartItems.map((c) => c._id) || [],
          page: `Shop - ${shopType}`,
        },
      });
    };
    userAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [queryParams, setQueryParams] = useState('');
  const [page, setPage] = useState(0);
  const [lastLocation, setLastLocation] = useState('');

  const updateProducts = async () => {
    const { data } = await axios.post(`/api/products/reload`, {
      productids: products.map((p) => p._id),
    });
    dispatch({ type: 'RELOAD_PRODUCTS', payload: data });
  };

  useEffect(() => {
    if (lastLocation !== window.location.pathname) {
      setLastLocation(window.location.pathname);
    }
    if (
      window.location.pathname.startsWith('/shop') &&
      !lastLocation.startsWith('/shop') &&
      products
    ) {
      updateProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    if (
      window.location.pathname.startsWith('/shop') &&
      !window.location.pathname.includes(
        `/shop/${shopType.toLowerCase()}/product/`
      ) &&
      products !== undefined &&
      (queryParams.toString() !== searchParams.toString() ||
        queryParams === '') &&
      !filterIsOpen
    ) {
      setQueryParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, window.location.pathname, filterIsOpen]);

  useEffect(() => {
    const fetchProducts = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        let query =
          [...new Set(queryParams.keys())].length > 0 ? `&${queryParams}` : ``;
        if (!query.includes('page=')) query += '&page=1';
        const products = await axios.get(
          `/api/products/search?type=${shopType}${query}`,
          {
            headers: {
              userid: id,
              cart: cart.cartItems.map((c) => c._id) || [],
              page: `Shop - ${shopType}`,
            },
          }
        );
        dispatch({ type: 'FETCH_SUCCESS', payload: products.data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: err.message });
      }
    };

    const fetchSettings = async () => {
      try {
        let query =
          [...new Set(queryParams.keys())].length > 0 ? `&${queryParams}` : ``;
        if (!query.includes('page=')) query += '&page=1';

        const productsCount = await axios.get(
          `/api/products/count?type=${shopType}&${query}`
        );
        const settings = await axios.get(`/api/settings/${shopType}`);
        const result = {
          productsCount:
            productsCount.data.length > 0 ? productsCount.data[0].amount : 0,
          settings: settings.data,
        };
        dispatch({ type: 'FETCH_SUCCESS_SETTINGS', payload: result });
        setPage(Number(queryParams.get('page')) || 1);
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: err.message });
      }
    };

    if (queryParams !== '') {
      fetchProducts();
      fetchSettings();
    }
    if (
      window.location.pathname.includes(
        `/shop/${shopType.toLowerCase()}/product/`
      )
    ) {
      dispatch({ type: 'STOP_LOADING' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleRefresh = () => {
    return new Promise((res) => {
      setTimeout(() => {
        res(window.location.reload());
      }, 1000);
    });
  };

  const fullUrl = queryParams
    ? `/shop/${shopType.toLowerCase()}?${queryParams}`
    : `/shop/${shopType.toLowerCase()}`;

  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const sizeFilter = sp.getAll('size');
  const sizeHatFilter = sp.getAll('sizeHat');
  const categoryFilter = sp.getAll('category');
  const brandFilter = sp.getAll('brand');

  const descriptionParameters = `${
    categoryFilter?.length > 0
      ? ` Kategorie${
          categoryFilter.length > 1 ? 'n' : ''
        }: ${categoryFilter.join(', ')}.`
      : ''
  }${
    sizeFilter?.length > 0
      ? ` Größe${sizeFilter.length > 1 ? 'n' : ''}: ${sizeFilter.join(', ')}.`
      : ''
  }${
    sizeHatFilter?.length > 0
      ? ` Hut-Größe${sizeHatFilter.length > 1 ? 'n' : ''}: ${sizeHatFilter.join(
          ', '
        )}.`
      : ''
  }${
    brandFilter?.length > 0
      ? ` Marke${brandFilter.length > 1 ? 'n' : ''}: ${brandFilter.join(', ')}.`
      : ''
  } Aktuelle Seite: ${page === 0 ? 1 : page}.`;

  const headerTagsShopKleidung = {
    title: 'Secondhand-Kleidung für Kinder | Nachhaltig shoppen',
    keywords:
      'Secondhand-Kinderkleidung, nachhaltige Kleidung, gebrauchte Kinderkleidung, BraunBärchen Shop',
    description: `Shoppe hochwertige Secondhand-Kleidung für Kinder in den Größen 50–128. Nachhaltig, geprüft und umweltfreundlich.${descriptionParameters}`,
    url: fullUrl,
  };

  const headerTagsShopSchlafsaecke = {
    title: 'Nachhaltige Schlafsäcke für Kinder',
    keywords:
      'Secondhand Schlafsäcke, Babyschlafsäcke, nachhaltige Schlafsäcke, BraunBärchen Shop',
    description: `Finde nachhaltige Secondhand-Schlafsäcke für Babys und Kinder. Hochwertig, hygienisch und geprüft. ${descriptionParameters}`,
    url: fullUrl,
  };

  const headerTagsShopAccessoires = {
    title: 'Nachhaltige Accessoires für Kinder',
    keywords:
      'Secondhand Accessoires, Kinder Accessoires, nachhaltige Accessoires, BraunBärchen Shop',
    description: `Entdecke nachhaltige Secondhand-Accessoires für Kinder. Von Mützen bis Taschen – nachhaltig und umweltfreundlich. ${descriptionParameters}`,
    url: fullUrl,
  };

  return (
    <Container fluid="md">
      {settings && (
        <Filter
          isMenuActive={filterIsOpen}
          onOverLayClick={() => {
            setFilterIsOpen(!filterIsOpen);
          }}
          filter={settings}
          setSearchParams={setSearchParams}
          type={shopType}
        />
      )}
      <div
        style={{ display: pathname.startsWith('/shop') ? 'block' : 'none' }}
        id="shopdiv"
      >
        <SeoUpdater
          headerTags={
            shopType === shopTypeEnum.Kleidung
              ? headerTagsShopKleidung
              : shopType === shopTypeEnum.Schlafsack
              ? headerTagsShopSchlafsaecke
              : headerTagsShopAccessoires
          }
        />
        {
          // loading ? (
          //   <Row className="d-flex justify-content-center">
          //     <div style={{ position: 'fixed', left: 0 }}>
          //       <LoadingBox />
          //     </div>
          //   </Row>
          // ) :
          error ? (
            <MessageBox variant="danger">{error}</MessageBox>
          ) : (
            <Outlet
              context={{
                shopType,
                products,
                productsCount,
                handleRefresh,
                setFilterIsOpen,
                filterIsOpen,
                page,
                loadingSettings,
              }}
            />
          )
        }
      </div>
    </Container>
  );
}
export default ShopScreen;
